/**
 * Reine Auswahl-Logik der Zwei-Ebenen-Branchenauswahl — ohne React/DOM, damit
 * sie per Vitest prüfbar ist.
 *
 * Fachliche Form: mehrere Branchen, je Branche mehrere Branchengruppen.
 * Gespeichert wird eine flache, geordnete Liste von Branchengruppen — die
 * ERSTE Zeile ist die Hauptbranche und steht zusätzlich am Kontakt
 * (`Contact.branchengruppeId` / `Contact.brancheFreitext`).
 *
 * WARUM die id und nie das Label: Die 171 Gruppennamen sind zwar über den
 * ganzen Katalog eindeutig, die Auffanggruppe „Sonstiges" aber kommt in jeder
 * Branche vor — 13-mal. Über den Namen ließe sie sich nicht auseinanderhalten.
 *
 * WARUM ein eigenes Modul neben `brancheAuswahlLogik.ts`: Deren `brancheClassId`
 * zeigt in den dreistufigen Altkatalog. Beide Kataloge zählen ihre Ids
 * unabhängig; ein gemeinsames Feld führte Altid 42 neben Neuid 42.
 */

import type { BrancheGruppe } from "../../../data/brancheGruppen";
import {
  erlaubtFreitext,
  findGruppe,
  formatGruppeMitFreitext,
  gruppenFuer,
  istAuffangBranche,
} from "../../../data/brancheGruppen";

/** Eine gewählte Branche = eine Katalog-Gruppe + optionaler „Sonstiges"-Freitext. */
export interface BrancheGruppenWahl {
  branchengruppeId: number;
  /**
   * Nur an Gruppen mit `erlaubtFreitext` („Sonstiges") gefüllt. Während der
   * Eingabe darf hier Rand-Leerraum stehen — getrimmt wird beim Vergleich
   * (`auswahlSignatur`) und beim Speichern (Backend).
   */
  freitext?: string | null;
}

/** Ein Eingabe-Block: EINE Branche (Ebene 1) mit ihren gewählten Gruppen. */
export interface BrancheGruppenBlock {
  ebene1: string;
  ids: number[];
}

/**
 * Verwirft ungültige ids und Dubletten. Der erste Treffer gewinnt, damit ein
 * bereits erfasster Freitext nicht von einer leeren Zweitzeile überschrieben
 * wird.
 *
 * ACHTUNG: Hier wird ein neues Objekt gebaut, nicht gespreadet. Jedes künftige
 * Feld muss also HIER mit aufgenommen werden — sonst verschwindet es zwischen
 * Laden und Speichern, ohne dass irgendetwas fehlschlägt.
 */
export function auswahlNormalisieren(auswahl: BrancheGruppenWahl[]): BrancheGruppenWahl[] {
  const gesehen = new Set<number>();
  const out: BrancheGruppenWahl[] = [];
  for (const a of auswahl ?? []) {
    const id = Number(a?.branchengruppeId);
    if (!Number.isFinite(id) || id <= 0 || gesehen.has(id)) continue;
    gesehen.add(id);
    out.push({ branchengruppeId: id, freitext: a.freitext ?? null });
  }
  return out;
}

/**
 * Eine Gruppe an-/abwählen. Neue landen am Ende — die Reihenfolge ist fachlich
 * relevant: die ERSTE Zeile ist die Hauptbranche.
 */
export function auswahlUmschalten(
  auswahl: BrancheGruppenWahl[],
  id: number,
): BrancheGruppenWahl[] {
  const drin = auswahl.some((a) => a.branchengruppeId === id);
  return drin
    ? auswahl.filter((a) => a.branchengruppeId !== id)
    : [...auswahl, { branchengruppeId: id, freitext: null }];
}

/** Eine Gruppe entfernen (samt ihrem Freitext). */
export function auswahlEntfernen(
  auswahl: BrancheGruppenWahl[],
  id: number,
): BrancheGruppenWahl[] {
  return auswahl.filter((a) => a.branchengruppeId !== id);
}

/** Den „Sonstiges"-Freitext EINER Zeile setzen; die übrigen bleiben unberührt. */
export function freitextSetzen(
  auswahl: BrancheGruppenWahl[],
  id: number,
  text: string,
): BrancheGruppenWahl[] {
  return auswahl.map((a) => (a.branchengruppeId === id ? { ...a, freitext: text } : a));
}

/**
 * Freitexte an Gruppen löschen, die keinen erlauben.
 *
 * Wer „Sonstiges" wählt, etwas schreibt und dann auf eine normale Gruppe
 * umschwenkt, hinterließe sonst einen Text, den die Maske nicht mehr zeigt und
 * den das Backend ohnehin verwirft — der Speichern-Knopf bliebe grundlos aktiv,
 * weil die Signatur weiter abweicht.
 */
export function freitextAufraeumen(
  rows: BrancheGruppe[],
  auswahl: BrancheGruppenWahl[],
): BrancheGruppenWahl[] {
  return auswahl.map((a) =>
    erlaubtFreitext(rows, a.branchengruppeId) ? a : { ...a, freitext: null },
  );
}

/** Eine Zeile zur Hauptbranche machen — sie wandert an Position 0. */
export function alsHauptbrancheSetzen(
  auswahl: BrancheGruppenWahl[],
  id: number,
): BrancheGruppenWahl[] {
  const treffer = auswahl.find((a) => a.branchengruppeId === id);
  if (!treffer) return auswahl;
  return [treffer, ...auswahl.filter((a) => a.branchengruppeId !== id)];
}

/** Die Hauptbranche = erste Zeile der Auswahl. */
export function hauptbranche(auswahl: BrancheGruppenWahl[]): BrancheGruppenWahl | undefined {
  return auswahl[0];
}

/** Anzeigepfad der Hauptbranche („Branche / Gruppe (Freitext)") oder "". */
export function hauptbranchePfad(
  rows: BrancheGruppe[],
  auswahl: BrancheGruppenWahl[],
): string {
  const h = hauptbranche(auswahl);
  if (!h) return "";
  return formatGruppeMitFreitext(findGruppe(rows, h.branchengruppeId), h.freitext);
}

/**
 * Vergleichsform für das Dirty-Tracking — Reihenfolge zählt (Hauptbranche!),
 * und der Freitext zählt mit.
 *
 * Ohne den Freitext bliebe der Speichern-Knopf nach einer reinen
 * Freitext-Änderung inaktiv, und die Eingabe wäre beim Verlassen der Akte weg.
 * Getrimmt wird hier, damit Rand-Leerraum allein keine Änderung ist.
 */
export function auswahlSignatur(auswahl: BrancheGruppenWahl[]): string {
  return JSON.stringify(
    auswahl.map((a) => [a.branchengruppeId, (a.freitext ?? "").trim()]),
  );
}

/**
 * Flache Auswahl → Blöcke, gruppiert nach Branche in Reihenfolge des ersten
 * Auftretens. Unbekannte ids (Katalog noch nicht geladen, Gruppe gelöscht)
 * sammeln sich in einem Block ohne Branche, statt still wegzufallen. Ohne
 * Auswahl bleibt EIN leerer Block stehen — die Maske zeigt immer mindestens
 * eine Branche.
 */
export function bloeckeAusAuswahl(
  rows: BrancheGruppe[],
  auswahl: BrancheGruppenWahl[],
): BrancheGruppenBlock[] {
  const bloecke: BrancheGruppenBlock[] = [];
  for (const a of auswahlNormalisieren(auswahl)) {
    const ebene1 = findGruppe(rows, a.branchengruppeId)?.ebene1 ?? "";
    const treffer = bloecke.find((b) => b.ebene1 === ebene1);
    if (treffer) treffer.ids.push(a.branchengruppeId);
    else bloecke.push({ ebene1, ids: [a.branchengruppeId] });
  }
  return bloecke.length > 0 ? bloecke : [{ ebene1: "", ids: [] }];
}

/**
 * Blöcke → flache Auswahl in Blockreihenfolge. Blöcke ohne Gruppe fallen weg:
 * eine Branche ohne Branchengruppe ist nicht speicherbar (die Zuordnung trägt
 * die Gruppen-id).
 *
 * `vorher` reicht die Freitexte des bisherigen Standes nach — ohne das wäre
 * jeder Freitext beim nächsten Klick in der Gruppenliste weg, ohne dass etwas
 * fehlschlägt.
 */
export function auswahlAusBloecken(
  bloecke: BrancheGruppenBlock[],
  vorher: BrancheGruppenWahl[] = [],
): BrancheGruppenWahl[] {
  const texte = new Map(vorher.map((a) => [a.branchengruppeId, a.freitext ?? null]));
  return auswahlNormalisieren(
    bloecke.flatMap((b) =>
      b.ids.map((id) => ({ branchengruppeId: id, freitext: texte.get(id) ?? null })),
    ),
  );
}

/** `auswahlUmschalten` auf den ids eines Blocks. */
export function idsUmschalten(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/**
 * Die Auffanggruppe („Sonstiges") ans ENDE eines Blocks, alle übrigen in
 * Klick-Reihenfolge.
 *
 * WARUM ÜBERHAUPT: Wählt der Anwender die Auffangbranche „Sonstige
 * (Landwirtschaft, Sport, Kultur, Brauchtum, Gesellschaft etc.)", hakt
 * `gruppeVorwaehlen` deren Auffanggruppe sofort an — nur an ihr kann der
 * Freitext hängen. Kreuzt er danach „Sport" an, hängt `idsUmschalten` das
 * hinten an, und „Sonstiges" bliebe auf Position 0. Die erste Zeile ist aber
 * die HAUPTbranche: Sie wandert als `branchengruppeId`/`industry` an den
 * Kontakt, und die Firma stünde in Listen, Suche und Auswertungen unter
 * „Sonstiges" statt unter „Sport" — sichtbar nur für den, der die Zeilen unter
 * den Dropdowns liest und dort „Als Hauptbranche" drückt.
 *
 * Ein Auffangeintrag ist ein AUFFANGbecken: Sobald eine benannte Gruppe im
 * selben Block steht, ist sie die aussagekräftigere Hauptbranche. Bleibt die
 * Auffanggruppe die einzige, steht sie weiter allein und damit vorn.
 *
 * `sort` ist seit ES2019 stabil — die übrige Klick-Reihenfolge bleibt also
 * erhalten.
 *
 * GRENZE, die jemand kennen muss: Wer die Auffanggruppe ausdrücklich über „Als
 * Hauptbranche" nach vorn holt, tut das über `alsHauptbrancheSetzen` auf der
 * flachen Auswahl — dort greift diese Ordnung nicht. Kreuzt er danach im
 * Gruppen-Dropdown noch etwas an oder ab, rutscht sie wieder nach hinten. Das
 * ist der seltenere Fall; der häufige (Auffangbranche wählen, dann die echte
 * Gruppe ankreuzen) muss ohne Nacharbeit richtig herauskommen.
 */
export function idsOrdnen(rows: BrancheGruppe[], ids: number[]): number[] {
  const rang = (id: number) => (erlaubtFreitext(rows, id) ? 1 : 0);
  return [...ids].sort((a, b) => rang(a) - rang(b));
}

/**
 * Die Gruppe, die eine Branche selbst setzt, weil sie ihre einzige ist.
 *
 * Betrifft die Auffangbranche „Sonstiges": Sie hat genau EINE Gruppe. Sie erst
 * noch anklicken zu lassen wäre eine leere Frage — der Anwender hat mit der
 * Branche schon alles gesagt.
 */
export function gruppeAutomatisch(rows: BrancheGruppe[], ebene1: string): number | null {
  if (!ebene1) return null;
  const gruppen = gruppenFuer(rows, ebene1);
  return gruppen.length === 1 ? gruppen[0].id : null;
}

/**
 * Die Gruppe, die eine Branche beim Wechsel selbst vorwählt.
 *
 * ZWEI FÄLLE, EIN GRUND — ohne gewählte Gruppe wird die Branche gar nicht
 * gespeichert (die Zuordnung trägt die Gruppen-id), und ein Freitext hätte
 * nichts, woran er hängen könnte:
 *
 * 1. Die Branche hat genau EINE Gruppe (`gruppeAutomatisch`) — sie erst noch
 *    anklicken zu lassen wäre eine leere Frage.
 * 2. Die Branche ist eine AUFFANGbranche. Der PO verlangt am 09.09.2026 ein
 *    Freitextfeld auch auf der Branchen-Ebene; einen eigenen Speicherort dafür
 *    gibt es im Datenmodell nicht (Freitext existiert ausschließlich je
 *    Zuordnung `{branchengruppeId, freitext}`). Die Auffang-GRUPPE derselben
 *    Branche ist dieser Ort — also wird sie mitgewählt, und ihr Freitextfeld
 *    erscheint. Wer statt „Sonstiges" doch „Sport" meint, hakt sie im
 *    Gruppen-Dropdown wieder ab.
 *
 * Erkannt wird die Auffanggruppe über `erlaubtFreitext`, NIE über den Namen:
 * Sieben ganz normale Katalogzeilen heißen „Sonstige …".
 */
export function gruppeVorwaehlen(rows: BrancheGruppe[], ebene1: string): number | null {
  if (!ebene1) return null;
  const einzige = gruppeAutomatisch(rows, ebene1);
  if (einzige != null) return einzige;
  if (!istAuffangBranche(ebene1)) return null;
  return gruppenFuer(rows, ebene1).find((g) => g.erlaubtFreitext === true)?.id ?? null;
}

/**
 * Beschriftung des GESCHLOSSENEN Branchengruppen-Dropdowns (PO 09.09.2026):
 * keine Auswahl → „keine ausgewählt", genau eine → der Gruppenname, mehrere →
 * „<n> ausgewählt".
 *
 * Das ist bewusst nur eine Zusammenfassung: WELCHE Gruppen gewählt sind und
 * welche davon die Hauptbranche ist, steht weiterhin in den Zeilen unter den
 * Dropdowns — dort hängen auch „Als Hauptbranche", „Entfernen" und das
 * Freitextfeld der Auffanggruppe.
 *
 * WARUM auch ohne Katalog gezählt wird: `rows` ist beim ersten Rendern leer
 * (der Katalog kommt über die API). „keine ausgewählt" zu zeigen behauptete
 * dann, die gespeicherte Auswahl sei weg — „1 ausgewählt" ist ehrlich.
 */
export function gruppenFeldBeschriftung(rows: BrancheGruppe[], ids: number[]): string {
  const gueltig = auswahlNormalisieren(
    (ids ?? []).map((id) => ({ branchengruppeId: id })),
  ).map((a) => a.branchengruppeId);
  if (gueltig.length === 0) return "keine ausgewählt";
  if (gueltig.length === 1) {
    const gruppe = findGruppe(rows, gueltig[0]);
    if (gruppe) return gruppe.ebene2;
  }
  return `${gueltig.length} ausgewählt`;
}
